import React, { useState } from "react";
import { Sparkles, Coins, Loader2, Zap, X, ArrowRight, Target, Bird, TrendingUp, Mail } from "lucide-react";
import { toast } from "sonner";
import { aiCreateGrowthPlus, aiCreateTopup, setAssessmentEmail } from "@/lib/gfApi";

export const CAMPAIGN_STATUS = {
  draft: { label: "Draf", cls: "bg-slate-100 text-slate-600" },
  strategy_ready: { label: "Strategi Sedia", cls: "bg-sky-100 text-sky-700" },
  generating: { label: "Menunggu Poster", cls: "bg-amber-100 text-amber-700" },
  completed: { label: "Kempen Lengkap", cls: "bg-emerald-100 text-emerald-700" },
  poster_failed: { label: "Poster Gagal — Cuba Semula", cls: "bg-rose-100 text-rose-700" },
};

export const readAiEmail = () => {
  const params = new URLSearchParams(window.location.search);
  return (params.get("email") || localStorage.getItem("gf_ai_email") || "").trim();
};

export const storeAiEmail = (email) => {
  if (email) localStorage.setItem("gf_ai_email", email.trim());
};

export const CreditChip = ({ credits, testId = "credit-chip" }) => {
  const usable = credits?.usable ?? 0;
  return (
    <span
      data-testid={testId}
      className="inline-flex items-center gap-1.5 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-3 py-1.5 text-xs font-bold text-[#B8912B]"
    >
      <Coins className="h-3.5 w-3.5" />
      {usable} Kredit Kempen
      {credits?.gp_active ? <span className="ml-1 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] text-emerald-700">Growth+</span> : null}
    </span>
  );
};

export const UpsellModal = ({ open, onClose, email, phone = "", assessmentId = "", zeroState = false }) => {
  const [loading, setLoading] = useState("");
  if (!open) return null;

  const buy = async (kind) => {
    if (!email) {
      toast.error("Emel diperlukan untuk pembelian.");
      return;
    }
    setLoading(kind);
    try {
      const body = { email, phone, assessment_id: assessmentId };
      const res = kind === "growthplus" ? await aiCreateGrowthPlus(body) : await aiCreateTopup(body);
      if (res.payment_url) {
        if (res.order_id) localStorage.setItem("gf_ai_last_order", res.order_id);
        window.location.assign(res.payment_url);
      } else throw new Error("no url");
    } catch (e) {
      setLoading("");
      toast.error(e?.response?.status === 503 ? "Pembayaran belum dikonfigurasi." : "Gagal memulakan pembayaran.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-6" data-testid="upsell-modal">
      <div className="w-full max-w-sm rounded-3xl bg-white p-6 shadow-2xl">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-xl font-bold text-[#0F172A]">
            {zeroState ? "Kredit Kempen Telah Digunakan" : "Tambah Kredit Kempen"}
          </h3>
          <button onClick={onClose} data-testid="upsell-close-btn" className="text-slate-400 hover:text-slate-600">
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="mt-1 text-sm text-slate-500">
          {zeroState
            ? "Starter campaign credits anda telah digunakan. Pilih pilihan di bawah untuk meneruskan kempen seterusnya."
            : "Setiap kempen penuh (strategi + poster + playbook) menggunakan 1 kredit."}
        </p>

        <button
          data-testid="buy-growthplus-btn"
          onClick={() => buy("growthplus")}
          disabled={!!loading}
          className="mt-5 w-full rounded-2xl bg-[#0F172A] p-5 text-left text-white transition-transform active:scale-[0.98] disabled:opacity-60"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-[#D4AF37]" />
              <span className="font-bold">Growth+</span>
            </div>
            <span className="font-heading text-2xl font-bold text-[#D4AF37]">RM39</span>
          </div>
          <p className="mt-1.5 text-sm text-slate-300">10 kredit kempen · sah 30 hari</p>
          {loading === "growthplus" && <Loader2 className="mt-2 h-4 w-4 animate-spin text-[#D4AF37]" />}
        </button>

        <button
          data-testid="buy-topup-btn"
          onClick={() => buy("topup")}
          disabled={!!loading}
          className="mt-3 w-full rounded-2xl border-2 border-[#E7E2D6] p-4 text-left transition-colors hover:border-[#D4AF37] disabled:opacity-60"
        >
          <div className="flex items-center justify-between">
            <span className="font-bold text-[#0F172A]">Top-up Sekali</span>
            <span className="font-heading text-xl font-bold text-[#0F172A]">RM5</span>
          </div>
          <p className="mt-1 text-sm text-slate-500">1 kredit kempen</p>
          {loading === "topup" && <Loader2 className="mt-2 h-4 w-4 animate-spin text-[#B8912B]" />}
        </button>
      </div>
    </div>
  );
};

export const AiCtaSection = ({ credits, assessmentId, email, onCreate, onViewAll }) => (
  <section className="mt-8 px-6" data-testid="ai-cta-section">
    <div className="overflow-hidden rounded-3xl bg-gradient-to-br from-[#0F172A] to-[#1E293B] p-6 text-white">
      <div className="flex items-center gap-2">
        <Sparkles className="h-5 w-5 text-[#D4AF37]" />
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#D4AF37]">Brand2Market AI</p>
      </div>
      <h3 className="mt-2 font-heading text-2xl font-bold text-white">Ubah Diagnosis Anda Kepada Tindakan</h3>
      <p className="mt-2 text-sm leading-relaxed text-slate-300">
        Jana kempen pemasaran lengkap — strategi, copywriting, pelan kandungan 7 hari, skrip jualan WhatsApp
        dan satu poster produk AI — berdasarkan diagnosis Growth Factor perniagaan anda.
      </p>
      <div className="mt-4 flex items-center gap-2">
        {credits ? <CreditChip credits={credits} /> : null}
      </div>
      {credits ? (
        <p className="mt-2 text-sm font-semibold text-[#D4AF37]" data-testid="cta-credit-balance">
          {credits.usable > 0
            ? `Anda ada ${credits.usable} kredit kempen untuk digunakan`
            : "Kredit kempen anda telah digunakan — beli Growth+ / top-up untuk teruskan"}
        </p>
      ) : null}
      <button
        data-testid="create-first-campaign-btn"
        onClick={onCreate}
        className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-[#D4AF37] py-3.5 text-sm font-bold text-[#0F172A] transition-transform active:scale-[0.98]"
      >
        Mulakan Kempen Berdasarkan Diagnosis Saya <ArrowRight className="h-4 w-4" />
      </button>
      <button
        data-testid="view-my-campaigns-btn"
        onClick={onViewAll}
        className="mt-3 w-full rounded-full border border-white/25 py-3 text-sm font-bold text-white transition-colors hover:bg-white/10"
      >
        Kempen Saya
      </button>
    </div>
  </section>
);

// Compact "DARIPADA DIAGNOSIS → TINDAKAN" context card shown inside Brand2Market.
export const GrowthContextCard = ({ ctx, priorityText, onReview }) => {
  if (!ctx) return null;
  return (
    <div className="rounded-2xl border border-[#E7E2D6] bg-white p-4" data-testid="growth-context-card">
      <div className="flex items-center gap-2">
        <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#B8912B]">Daripada Diagnosis → Tindakan</span>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2 text-center">
        <div className="rounded-xl bg-[#FFFBEF] py-2.5">
          <TrendingUp className="mx-auto h-4 w-4 text-[#B8912B]" />
          <p className="mt-1 font-heading text-lg font-bold text-[#0F172A]" data-testid="ctx-score">{ctx.growth_score}</p>
          <p className="text-[10px] text-slate-500">Growth Score</p>
        </div>
        <div className="rounded-xl bg-[#FFFBEF] py-2.5">
          <Bird className="mx-auto h-4 w-4 text-[#B8912B]" />
          <p className="mt-1 text-[11px] font-bold leading-tight text-[#0F172A]" data-testid="ctx-archetype">{ctx.archetype}</p>
          <p className="text-[10px] text-slate-500">Archetype</p>
        </div>
        <div className="rounded-xl bg-[#FFFBEF] py-2.5">
          <Target className="mx-auto h-4 w-4 text-[#B8912B]" />
          <p className="mt-1 text-[11px] font-bold leading-tight text-[#0F172A]" data-testid="ctx-bottleneck">{ctx.bottleneck}</p>
          <p className="text-[10px] text-slate-500">#1 Halangan</p>
        </div>
      </div>
      <p className="mt-3 text-sm leading-relaxed text-slate-600">
        <b className="text-[#0F172A]">Keutamaan anda sekarang: </b>{priorityText || ctx.priority}
      </p>
      {onReview && (
        <button onClick={onReview} data-testid="review-report-link" className="mt-2 text-xs font-semibold text-[#B8912B] underline">
          Lihat Semula Laporan Business Growth
        </button>
      )}
    </div>
  );
};

// One-time email capture for existing assessments with no email on record.
export const EmailCaptureModal = ({ open, assessmentId, onSaved }) => {
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  if (!open) return null;

  const save = async () => {
    const v = email.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) {
      toast.error("Sila masukkan emel yang sah.");
      return;
    }
    setSaving(true);
    try {
      const res = await setAssessmentEmail(assessmentId, v);
      localStorage.setItem("gf_ai_email", res.email || v);
      toast.success("Terima kasih! Emel anda telah disimpan.");
      onSaved(res.email || v);
    } catch (e) {
      setSaving(false);
      toast.error(e?.response?.data?.detail || "Gagal menyimpan emel.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-6" data-testid="email-capture-modal">
      <div className="w-full max-w-sm rounded-3xl bg-white p-6 shadow-2xl">
        <div className="flex items-center gap-2">
          <Mail className="h-5 w-5 text-[#D4AF37]" />
          <h3 className="font-heading text-xl font-bold text-[#0F172A]">Ke mana kami perlu hantar laporan anda?</h3>
        </div>
        <p className="mt-2 text-sm text-slate-500">
          Masukkan emel anda untuk menerima laporan Business Growth dan hasil kempen Brand2Market. Anda tidak perlu mengulang penilaian.
        </p>
        <input
          data-testid="email-capture-input"
          type="email"
          className="mt-4 w-full rounded-2xl border border-[#E7E2D6] px-4 py-3.5 text-[15px] outline-none focus:border-[#D4AF37]"
          placeholder="emel@contoh.com" value={email} onChange={(e) => setEmail(e.target.value)}
        />
        <button
          data-testid="email-capture-submit" onClick={save} disabled={saving}
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-[#D4AF37] py-3.5 text-sm font-bold text-[#0F172A] disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan & Teruskan"}
        </button>
      </div>
    </div>
  );
};
